import * as THREE from 'three'

export class Renderer {
  public scene: THREE.Scene
  public camera: THREE.PerspectiveCamera
  private renderer: THREE.WebGLRenderer
  private canvas: HTMLCanvasElement

  constructor() {
    // Get canvas element
    this.canvas = document.getElementById('canvas') as HTMLCanvasElement
    if (!this.canvas) {
      throw new Error('Canvas element not found')
    }

    // Create scene with transparent background (camera shows through)
    this.scene = new THREE.Scene()
    this.scene.background = null

    // Create camera (orthographic-like perspective for 2D feel)
    // Position camera so visible area at z=0 matches screen dimensions
    const aspect = window.innerWidth / window.innerHeight
    const fov = 60
    this.camera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 5000)
    // Calculate z so that visible height at z=0 equals screen height
    // visibleHeight = 2 * z * tan(fov/2) → z = height / (2 * tan(fov/2))
    this.camera.position.z = window.innerHeight / (2 * Math.tan((fov * Math.PI / 180) / 2))

    // Create WebGL renderer with transparency
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true
    })
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    // Handle window resize
    window.addEventListener('resize', this.onResize)
  }

  private onResize = (): void => {
    const width = window.innerWidth
    const height = window.innerHeight

    this.camera.aspect = width / height
    // Update camera z to maintain screen-to-world mapping
    const fov = 60
    this.camera.position.z = height / (2 * Math.tan((fov * Math.PI / 180) / 2))
    this.camera.updateProjectionMatrix()

    this.renderer.setSize(width, height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  }

  public render(): void {
    this.renderer.render(this.scene, this.camera)
  }

  public dispose(): void {
    window.removeEventListener('resize', this.onResize)
    this.renderer.dispose()
  }

  // Convert screen coordinates to Three.js world coordinates
  public screenToWorld(screenX: number, screenY: number): THREE.Vector3 {
    const vector = new THREE.Vector3()

    // Convert to normalized device coordinates (-1 to +1)
    vector.x = (screenX / window.innerWidth) * 2 - 1
    vector.y = -(screenY / window.innerHeight) * 2 + 1
    vector.z = 0.5

    // Unproject to world coordinates
    vector.unproject(this.camera)

    // Get direction from camera
    const dir = vector.sub(this.camera.position).normalize()
    const distance = -this.camera.position.z / dir.z
    const pos = this.camera.position.clone().add(dir.multiplyScalar(distance))

    return pos
  }
}
